import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export function PageLoader({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    // Minimum visible duration so the brand reveal animation is perceptible.
    const MIN_VISIBLE_MS = 1600;
    // Hard cap so a slow/blocked font fetch never strands the loader on screen.
    const MAX_VISIBLE_MS = 2600;

    const finish = () => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
      setTimeout(() => {
        if (!cancelled) setIsLoading(false);
      }, remaining);
    };

    const safetyTimer = setTimeout(() => {
      if (!cancelled) setIsLoading(false);
    }, MAX_VISIBLE_MS);

    if (typeof document !== "undefined" && (document as any).fonts?.ready) {
      (document as any).fonts.ready.then(() => {
        requestAnimationFrame(finish);
      });
    } else {
      finish();
    }

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="fixed inset-0 z-[100] bg-background flex items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-col items-center gap-6"
            >
              <span className="font-logo text-2xl md:text-3xl tracking-widest text-foreground">
                SAILKHA GROUP
              </span>
              <motion.div
                className="w-12 h-px bg-foreground/30"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Render children immediately so the hero H1 can be the LCP element.
          The loader overlay above sits on top until fonts are ready. */}
      <div>{children}</div>
    </>
  );
}
